import React, {useState, useEffect} from 'react'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Box,
    Text,
    Radio,
    RadioGroup,
    Stack as ChakraStack,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useHistory} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {remCalc} from '../../theme/_utils'
import {CloseIcon} from '../icons'

const SubscriptionEditBillingAddressModal = ({
    isOpen,
    onClose,
    currentAddress,
    subscriptionId,
    onSuccess,
}) => {
    const {data: customer} = useCurrentCustomer()
    const [selectedAddress, setSelectedAddress] = useState('current')
    const {getTokenWhenReady} = useAccessToken()
    const [billingAddresses, setBillingAddresses] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [message, setMessage] = useState('')
    const appOrigin = useOrigin(false)
    const history = useHistory()
    const {locale} = useMultiSite()
    const localeId = locale?.alias
    const styles = useMultiStyleConfig('Subscriptions')

    useEffect(() => {
        if (customer?.addresses) {
            // Only billing addresses (B2B or B2C billing)
            const billing = customer.addresses.filter(
                (address) => address?.c_isB2CBillingAddress || address?.c_isB2BAddress,
            )
            setBillingAddresses((prev) => {
                const prevIds = prev.map((a) => a.addressId).join(',')
                const nextIds = billing.map((a) => a.addressId).join(',')
                if (prevIds !== nextIds) {
                    return billing
                }
                return prev
            })

            // Always preselect the first address (default behaviour)
            if (billing.length > 0) {
                setSelectedAddress(billing[0].addressId)
            } else {
                setSelectedAddress('current')
            }
        }
        // Only run when customer.addresses changes
    }, [customer?.addresses])

    // Helper to get the selected billing address details
    const getSelectedBillingDetails = () => {
        const addr = billingAddresses.find((a) => a.addressId === selectedAddress)
        if (addr) {
            return {
                Billing__City__s: addr.city,
                Billing__CountryCode__s: addr.countryCode,
                Billing__PostalCode__s: addr.postalCode,
                Billing__StateCode__s: addr.stateCode,
                Billing__Street__s: addr.address1,
                CompanyName__c: addr.c_isB2BAddress ? addr.c_companyName : '', // Only set for B2B addresses
                FiscalCode__c: addr.c_isB2BAddress ? addr.c_fiscalCode : '', // Only set for B2B addresses
                PurchaseType__c: addr.c_isB2BAddress ? 'HB' : 'H', // HB for B2B, H for Standard
                SDI__c: addr.c_sdi,
                VAT__c: addr.c_vatNumber,
            }
        }
        return {}
    }

    // Handle the confirm edit billing address action
    const handleEditBillingAddress = async () => {
        try {
            setIsLoading(true)
            const token = await getTokenWhenReady()

            const billingDetails = getSelectedBillingDetails()

            const patchPayload = {
                subscriptionId,
                allOrNone: true,
                compositeRequest: [
                    {
                        method: 'PATCH',
                        url: `/services/data/v59.0/sobjects/Subscription__c/${subscriptionId}`,
                        referenceId: 'refAccount',
                        body: billingDetails,
                    },
                ],
            }

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/update-subscription-billing-address-oms`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        customerId: customer?.customerId,
                        'x-commercecloud-token': `Bearer ${token}`,
                    },
                    body: JSON.stringify(patchPayload),
                },
            )

            const result = await response.json()
            if (result.success) {
                setMessage('Billing address updated successfully')
                onClose()
                if (typeof onSuccess === 'function') {
                    onSuccess()
                }
            } else {
                setMessage('Error during billing address update: ' + result.error)
            }
        } catch (error) {
            console.error('Error during billing address update:', error)
            setMessage('Error during billing address update: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent padding="40px 24px">
                <ModalHeader {...styles.subscriptionEditHeaderContent}>
                    <FormattedMessage
                        id="subscription_edit_billing_address.title"
                        defaultMessage="Billing address"
                    />
                </ModalHeader>
                <ModalCloseButton {...styles.modalClose} top={remCalc(36)}>
                    <CloseIcon {...styles.modalCloseIcon} />
                </ModalCloseButton>
                <ModalBody {...styles.subscriptionEditBodyContent}>
                    <Text mb={4} {...styles.infoValue}>
                        <FormattedMessage
                            id="subscription_edit_billing_address.desc"
                            defaultMessage="Edit the billing address. The new billing address will be active starting from the next order"
                        />
                    </Text>
                    <RadioGroup value={selectedAddress} onChange={setSelectedAddress}>
                        <ChakraStack gap={4}>
                            {/* Customer billing addresses */}
                            {billingAddresses
                                .filter(
                                    (addr) =>
                                        `${addr.firstName || ''} ${addr.lastName || ''}`.trim() !==
                                            currentAddress?.name?.trim() ||
                                        addr.street !== currentAddress?.street,
                                )
                                .map((addr) => (
                                    <Box
                                        as="label"
                                        key={addr.addressId}
                                        border="1px solid"
                                        borderColor="border.dark"
                                        borderRadius="md"
                                        p={4}
                                        w="100%"
                                        cursor="pointer"
                                        _checked={{borderColor: 'border.dark'}}
                                    >
                                        <Radio
                                            value={addr.addressId}
                                            mr={4}
                                            {...styles.subscriptionEditRadio}
                                        >
                                            <Text as="span" fontWeight="bold">
                                                <FormattedMessage
                                                    id="subscription_edit_billing_address.use_this"
                                                    defaultMessage="Use this address"
                                                />
                                            </Text>
                                        </Radio>
                                        <Box mt={2}>
                                            <Text fontWeight="bold">{addr.fullName}</Text>
                                            <Text>{addr.phone}</Text>
                                            <Text>{addr.address1}</Text>
                                            <Text>{addr.street}</Text>
                                            <Text>
                                                {addr.city}, {addr.postalCode}
                                            </Text>
                                        </Box>
                                    </Box>
                                ))}
                        </ChakraStack>
                    </RadioGroup>
                    {message && (
                        <Text color="red.500" mt={4}>
                            {message}
                        </Text>
                    )}
                </ModalBody>
                <ModalFooter
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    paddingBottom="0px"
                    paddingX={0}
                >
                    <Button
                        variant="secondary"
                        width="100%"
                        mb={4}
                        onClick={() =>
                            history.push({
                                pathname: `/${localeId}/account/addresses`,
                                state: {scrollTo: 'billing'},
                            })
                        }
                    >
                        <FormattedMessage
                            id="subscription_edit_billing_address.create_new"
                            defaultMessage="Create new billing address"
                        />
                    </Button>
                    <Button
                        variant="primary"
                        width="100%"
                        onClick={handleEditBillingAddress}
                        isLoading={isLoading}
                    >
                        <FormattedMessage
                            id="subscription_edit_billing_address.confirm"
                            defaultMessage="Confirm"
                        />
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

SubscriptionEditBillingAddressModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentAddress: PropTypes.shape({
        name: PropTypes.string,
        phone: PropTypes.string,
        street: PropTypes.string,
        city: PropTypes.string,
        postalCode: PropTypes.string,
        countryCode: PropTypes.string,
        stateCode: PropTypes.string,
        address1: PropTypes.string,
        companyName: PropTypes.string,
        c_fiscalCode: PropTypes.string,
        c_purchaseType: PropTypes.string,
        c_sdi: PropTypes.string,
        c_vatNumber: PropTypes.string,
    }),
    subscriptionId: PropTypes.string,
    onSuccess: PropTypes.func,
}

export default SubscriptionEditBillingAddressModal
